import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Star, Plus, Trash2, GripVertical, Calendar, Eye, EyeOff, Search, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { FEATURED_SLOTS, FEATURED_KINDS, kindLabel } from "@/lib/featuredTypes";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function resolveImage(p) {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  if (p.startsWith("/api/")) return p; // already routed
  return `${API}/img/${p}?w=1600&q=86`;
}

export default function AdminFeatured() {
  const [activeSlot, setActiveSlot] = useState(FEATURED_SLOTS[0].value);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/featured", { params: { slot: activeSlot } });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [activeSlot]);

  const onAdd = async (kind, target_id) => {
    try {
      await api.post("/admin/featured", { kind, target_id, slot: activeSlot, active: true });
      toast.success("Mise en avant ajoutée");
      setPickerOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e?.userMessage || "Échec de l'ajout");
    }
  };

  const onToggle = async (it) => {
    try {
      const { data } = await api.patch(`/admin/featured/${it.id}`, { active: !it.active });
      setItems((prev) => prev.map((p) => (p.id === it.id ? data : p)));
    } catch (e) { toast.error(e?.userMessage || "Échec"); }
  };

  const onDelete = async (it) => {
    if (!window.confirm("Retirer cette mise en avant ?")) return;
    try {
      await api.delete(`/admin/featured/${it.id}`);
      setItems((prev) => prev.filter((p) => p.id !== it.id));
      toast.success("Retirée");
    } catch (e) { toast.error(e?.userMessage || "Échec"); }
  };

  const onSetExpiry = async (it, isoDate) => {
    try {
      const expires_at = isoDate ? new Date(isoDate).toISOString() : null;
      const { data } = await api.patch(`/admin/featured/${it.id}`, { expires_at });
      setItems((prev) => prev.map((p) => (p.id === it.id ? data : p)));
      toast.success(expires_at ? "Date d'expiration mise à jour" : "Expiration retirée");
    } catch (e) { toast.error(e?.userMessage || "Échec"); }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const newOrder = arrayMove(items, oldIndex, newIndex);
    setItems(newOrder);
    try {
      await api.put("/admin/featured/reorder", {
        slot: activeSlot,
        ordered_ids: newOrder.map((i) => i.id),
      });
    } catch {
      fetchItems(); // rollback
      toast.error("Réordonnancement échoué");
    }
  };

  const slotMeta = useMemo(() => FEATURED_SLOTS.find((s) => s.value === activeSlot), [activeSlot]);

  return (
    <div data-testid="admin-featured-page">
      <div className="mb-8">
        <p className="caption-label text-[#A26F3E]">Curation</p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-1 leading-[1.05]">Mises en avant</h1>
        <p className="text-[#666] mt-2">Choisissez ce qui apparaît en priorité sur chaque surface de la plateforme.</p>
      </div>

      {/* Slot tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="featured-slot-tabs">
        {FEATURED_SLOTS.map((s) => {
          const active = s.value === activeSlot;
          return (
            <button
              key={s.value}
              onClick={() => setActiveSlot(s.value)}
              className={`px-4 py-2 rounded-full text-sm border transition-all ${
                active
                  ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                  : "bg-white text-[#1A1A1A] border-[#EADDC9] hover:border-[#1A1A1A]"
              }`}
              data-testid={`slot-tab-${s.value}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="card-lux p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="font-serif text-2xl text-[#1A1A1A]">{slotMeta?.label}</h2>
            <p className="text-xs text-[#8B8680] mt-1">{slotMeta?.desc}</p>
          </div>
          <Button
            onClick={() => setPickerOpen(true)}
            className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white"
            data-testid="featured-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Ajouter
          </Button>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-[#C68E59] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center" data-testid="featured-empty">
            <Star className="w-8 h-8 text-[#C68E59] mx-auto" />
            <p className="font-serif text-xl text-[#1A1A1A] mt-3">Rien en avant pour l'instant</p>
            <p className="text-sm text-[#666] mt-1">Cliquez sur "Ajouter" pour mettre en avant un contenu sur cette surface.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3" data-testid="featured-list">
                {items.map((it) => (
                  <FeaturedRow key={it.id} item={it} onToggle={onToggle} onDelete={onDelete} onSetExpiry={onSetExpiry} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <FeaturedPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onAdd={onAdd} />
    </div>
  );
}

function FeaturedRow({ item, onToggle, onDelete, onSetExpiry }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const e = item.entity || {};
  const title = e.title || e.company_name || e.name || `${kindLabel(item.kind)} ${item.target_id.slice(0, 8)}`;
  const subtitle = e.theme || e.pro_type || e.talent_type || e.location || "";
  const img = e.image_url || e.cover_image || e.logo_url || e.picture || (e.gallery_images && e.gallery_images[0]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-4 rounded-2xl border bg-white ${
        isDragging ? "border-[#C68E59] shadow-lg" : "border-[#EADDC9]"
      }`}
      data-testid={`featured-row-${item.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-[#8B8680] hover:text-[#1A1A1A] p-1"
        aria-label="Réordonner"
        data-testid={`featured-handle-${item.id}`}
      >
        <GripVertical className="w-5 h-5" />
      </button>

      {img ? (
        <img src={resolveImage(img)} alt="" className="w-16 h-16 rounded-xl object-cover border border-[#EADDC9]" />
      ) : (
        <div className="w-16 h-16 rounded-xl bg-[#FAF3EA] flex items-center justify-center text-[#C68E59] border border-[#EADDC9]">
          <Star className="w-5 h-5" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#FAF3EA] text-[#A26F3E]">
            {kindLabel(item.kind)}
          </span>
          <p className="font-serif text-base text-[#1A1A1A] truncate">{title}</p>
        </div>
        {subtitle && <p className="text-xs text-[#8B8680] mt-0.5 truncate">{subtitle}</p>}
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-xs text-[#666]">
            <Calendar className="w-3.5 h-3.5 text-[#C68E59]" />
            <input
              type="date"
              value={item.expires_at ? item.expires_at.slice(0, 10) : ""}
              onChange={(ev) => onSetExpiry(item, ev.target.value || null)}
              className="bg-transparent border-b border-[#EADDC9] focus:border-[#C68E59] outline-none text-xs text-[#1A1A1A]"
              data-testid={`featured-expiry-${item.id}`}
            />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onToggle(item)}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
            item.active
              ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
              : "bg-white text-[#666] border-[#EADDC9]"
          }`}
          data-testid={`featured-toggle-${item.id}`}
        >
          {item.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={() => onDelete(item)}
          className="p-2 rounded-full text-[#B44F3F] hover:bg-[#FAF3EA]"
          aria-label="Supprimer"
          data-testid={`featured-delete-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function FeaturedPicker({ open, onClose, onAdd }) {
  const [kind, setKind] = useState("concours");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBusy(true);
    const fetchPath = {
      concours: "/concours",
      creator:  "/creators",
      talent:   "/talents",
      collab:   "/collaborations",
      event:    "/events",
    }[kind];
    api.get(fetchPath, { params: search ? { q: search } : {} })
      .then(({ data }) => setResults(Array.isArray(data) ? data.slice(0, 30) : []))
      .catch(() => setResults([]))
      .finally(() => setBusy(false));
  }, [open, kind, search]);

  const optionId = (it) => it.id || it.user_id || it._id;
  const optionTitle = (it) => it.title || it.company_name || it.name || "?";
  const optionImg = (it) => it.image_url || it.cover_image || it.logo_url || it.picture || (it.gallery_images && it.gallery_images[0]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="featured-picker-dialog">
        <DialogHeader><DialogTitle>Mettre en avant un contenu</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="rounded-full" data-testid="picker-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FEATURED_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="sm:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8680]" />
              <Input
                placeholder="Rechercher…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-full pl-9"
                data-testid="picker-search"
              />
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto space-y-2 pr-1" data-testid="picker-results">
            {busy ? (
              <div className="py-8 text-center text-[#8B8680]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : results.length === 0 ? (
              <p className="text-sm text-[#8B8680] text-center py-8">Aucun résultat</p>
            ) : results.map((r) => {
              const id = optionId(r);
              if (!id) return null;
              const img = optionImg(r);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onAdd(kind, id)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#EADDC9] hover:border-[#C68E59] hover:bg-[#FAF3EA] transition-colors text-left"
                  data-testid={`picker-result-${id}`}
                >
                  {img ? (
                    <img src={resolveImage(img)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-[#FAF3EA] flex items-center justify-center text-[#C68E59]"><Star className="w-4 h-4" /></div>
                  )}
                  <span className="flex-1 text-sm text-[#1A1A1A] truncate">{optionTitle(r)}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#8B8680]">+ Ajouter</span>
                </button>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
